/**
 * Role Management Service
 * Handles role and permission management with Supabase integration
 */

import { supabase } from './supabaseClient'
import { createLogger } from './logger'
import { invokeEdgeFunction } from './edgeFunctionClient'

const log = createLogger('RoleManagement')

export interface Role {
  id: string
  name: string
  description: string
  created_at: string
  updated_at: string
  permissions?: Permission[]
}

export interface Permission {
  id: string
  name: string
  description: string
  resource: string
  action: string
  created_at: string
}

export interface UserRole {
  id: string
  user_id: string
  role_id: string
  assigned_by?: string
  assigned_at: string
  expires_at?: string
  role: Role
}

export interface RolePermission {
  id: string
  role_id: string
  permission_id: string
  permission: Permission
}

class RoleManagementService {
  /**
   * Get all roles with their permissions
   */
  async getRoles(): Promise<Role[]> {
    try {
      const { data, error } = await supabase
        .from('roles')
        .select(
          `
          *,
          role_permissions (
            permission:permissions (*)
          )
        `,
        )
        .order('name')

      if (error) throw error

      // Transform the data to include permissions array
      return data.map((role) => ({
        ...role,
        permissions: role.role_permissions?.map((rp: any) => rp.permission) || [],
      }))
    } catch (error) {
      log.error('Failed to get roles:', error)
      throw error
    }
  }

  /**
   * Get all permissions
   */
  async getPermissions(): Promise<Permission[]> {
    try {
      const { data, error } = await supabase
        .from('permissions')
        .select('*')
        .order('resource', { ascending: true })
        .order('action', { ascending: true })

      if (error) throw error
      return data
    } catch (error) {
      log.error('Failed to get permissions:', error)
      throw error
    }
  }

  /**
   * Create a new role
   */
  async createRole(roleData: { name: string; description: string }): Promise<Role> {
    try {
      const { data, error } = await supabase.from('roles').insert(roleData).select().single()

      if (error) throw error
      return data
    } catch (error) {
      log.error('Failed to create role:', error)
      throw error
    }
  }

  /**
   * Update a role
   */
  async updateRole(
    roleId: string,
    roleData: { name?: string; description?: string },
  ): Promise<Role> {
    try {
      const { data, error } = await supabase
        .from('roles')
        .update({ ...roleData, updated_at: new Date().toISOString() })
        .eq('id', roleId)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      log.error('Failed to update role:', error)
      throw error
    }
  }

  /**
   * Delete a role
   */
  async deleteRole(roleId: string): Promise<void> {
    try {
      const { error } = await supabase.from('roles').delete().eq('id', roleId)

      if (error) throw error
    } catch (error) {
      log.error('Failed to delete role:', error)
      throw error
    }
  }

  /**
   * Assign permissions to a role
   */
  async assignPermissionsToRole(roleId: string, permissionIds: string[]): Promise<void> {
    try {
      // First, remove all existing permissions for this role
      await supabase.from('role_permissions').delete().eq('role_id', roleId)

      // Then add the new permissions
      if (permissionIds.length > 0) {
        const rolePermissions = permissionIds.map((permissionId) => ({
          role_id: roleId,
          permission_id: permissionId,
        }))

        const { error } = await supabase.from('role_permissions').insert(rolePermissions)

        if (error) throw error
      }
    } catch (error) {
      log.error('Failed to assign permissions to role:', error)
      throw error
    }
  }

  /**
   * Get user roles for a specific user
   */
  async getUserRoles(userId: string): Promise<UserRole[]> {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select(
          `
          *,
          role:roles (*)
        `,
        )
        .eq('user_id', userId)
        .order('assigned_at', { ascending: false })

      if (error) throw error
      return data
    } catch (error) {
      log.error('Failed to get user roles:', error)
      throw error
    }
  }

  /**
   * Assign a role to a user
   */
  async assignRoleToUser(userId: string, roleId: string, assignedBy?: string): Promise<UserRole> {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .insert({
          user_id: userId,
          role_id: roleId,
          assigned_by: assignedBy,
        })
        .select(
          `
          *,
          role:roles (*)
        `,
        )
        .single()

      if (error) throw error
      return data
    } catch (error) {
      log.error('Failed to assign role to user:', error)
      throw error
    }
  }

  /**
   * Remove a role from a user
   */
  async removeRoleFromUser(userId: string, roleId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('role_id', roleId)

      if (error) throw error
    } catch (error) {
      log.error('Failed to remove role from user:', error)
      throw error
    }
  }

  /**
   * Check if user has a specific permission
   */
  async userHasPermission(userId: string, permissionName: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('user_has_permission', {
        user_id_param: userId,
        permission_name_param: permissionName,
      })

      if (error) throw error
      return data || false
    } catch (error) {
      log.error('Failed to check user permission:', error)
      return false
    }
  }

  /**
   * Check if user has a specific role
   */
  async userHasRole(userId: string, roleName: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('user_has_role', {
        user_id_param: userId,
        role_name_param: roleName,
      })

      if (error) throw error
      return data || false
    } catch (error) {
      log.error('Failed to check user role:', error)
      return false
    }
  }

  /**
   * Get all users with their roles
   */
  async getUsersWithRoles(): Promise<any[]> {
    try {
      // Always prefer IdP list (Auth0) so we see the full directory in prod
      // Prefer ID token for email claim; fall back to access token
      let token = (window as any).__auth0IdToken as string | undefined
      if (!token) {
        // Wait briefly for Auth0 token (up to ~1.5s)
        for (let i = 0; i < 15 && !token; i++) {
          await new Promise((r) => setTimeout(r, 100))
          token = (window as any).__auth0IdToken || (window as any).__auth0Token
        }
      }

      let authUsers: any[] = []

      const { data: auth0Data, error: auth0Error } = await invokeEdgeFunction(
        'auth0-management-users',
        { page: 0, per_page: 100, token },
      )

      if (!auth0Error && Array.isArray((auth0Data as any)?.users)) {
        authUsers = (auth0Data as any).users
      }

      if (!Array.isArray(authUsers) || authUsers.length === 0) {
        const rpc = await supabase.rpc('get_all_user_roles')
        if (!rpc.error && Array.isArray(rpc.data)) {
          authUsers = rpc.data
        }
      }

      // Get all user roles from Supabase
      const { data: userRoles, error } = await supabase.from('user_roles').select(`
          user_id,
          role:roles (*)
        `)

      if (error) throw error

      // Build email → user_id mapping from admin_users so we can match
      // Auth0 users (who have Auth0 IDs) to Supabase user_roles (which use UUIDs)
      const { data: adminRows } = await supabase
        .from('admin_users')
        .select('user_id, email')

      const emailToUserId = new Map<string, string>()
      ;(adminRows || []).forEach((row: any) => {
        if (row.email && row.user_id) {
          emailToUserId.set(row.email.toLowerCase(), row.user_id)
        }
      })

      // Group roles by user ID
      const rolesByUserId = new Map<string, any[]>()
      userRoles.forEach((ur: any) => {
        if (!rolesByUserId.has(ur.user_id)) {
          rolesByUserId.set(ur.user_id, [])
        }
        if (ur.role) {
          rolesByUserId.get(ur.user_id)!.push(ur.role)
        }
      })

      // Also build email → roles mapping for cross-reference
      const rolesByEmail = new Map<string, any[]>()
      emailToUserId.forEach((userId, email) => {
        const roles = rolesByUserId.get(userId)
        if (roles) {
          rolesByEmail.set(email, roles)
        }
      })

      // Combine users with their Supabase roles (match by id, then by email)
      return authUsers.map((user: any) => {
        const id = user.id || user.user_id
        const email = (user.email || '').toLowerCase()
        // Try direct UUID match first
        let roles = rolesByUserId.get(id)
        // Fall back to email-based match (Auth0 id ≠ Supabase UUID)
        if (!roles && email) {
          roles = rolesByEmail.get(email)
          // Also try resolving email → UUID → roles
          if (!roles) {
            const resolvedId = emailToUserId.get(email)
            if (resolvedId) {
              roles = rolesByUserId.get(resolvedId)
            }
          }
        }
        return {
          ...user,
          roles: roles || [],
        }
      })
    } catch (error) {
      log.error('Failed to get users with roles:', error)
      throw error
    }
  }
}

// Export singleton instance
export const roleManagementService = new RoleManagementService()
